/* eslint-disable vue/multi-word-component-names */

import { createApp } from 'vue';
import router from './router';

import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import App from './App.vue';
import { createI18n } from './i18n';
import { createPinia } from 'pinia';
import { useUnsavedChangesStore } from '@/stores/unsavedChanges';
import { useUserStore } from '@/stores/user';
import api from '@/services/axios';

import VueGridLayout from 'vue-grid-layout';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import '@vue-js-cron/prime/dist/prime.css';

// Prime-Komponenten (global)
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import MultiSelect from 'primevue/multiselect';
import Tag from 'primevue/tag';
import InputIcon from 'primevue/inputicon';
import IconField from 'primevue/iconfield';
import Checkbox from 'primevue/checkbox';
import Select from 'primevue/select';
import Editor from 'primevue/editor';
import InputText from 'primevue/inputtext';
import pButton from 'primevue/button';
import pCard from 'primevue/card';
import pFloatLabel from 'primevue/floatlabel';
import pTooltip from 'primevue/tooltip';
import pDivider from 'primevue/divider';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import ConfirmDialog from 'primevue/confirmdialog';
import pFieldset from 'primevue/fieldset'
import AppTable       from '@/components/ui/AppTable.vue'
import AppTableWidget from '@/components/ui/AppTableWidget.vue'
import WidgetNumber   from '@/components/ui/WidgetNumber.vue'


// CSS: base.css = Reset + Design-Tokens; main.css = Komponenten-Overrides + Layout
import './assets/base.css'
import './assets/main.css'
import './assets/platform-ios.css' // iOS-spezifisch — wirkt nur bei body.platform-ios

import { tokenStore } from '@/services/tokenStore'

// ⚡️ Vorab Theme-Klasse auf <html> setzen, um FOUC zu vermeiden
;(function bootstrapTheme() {
  try {
    const root = document.documentElement
    const saved = localStorage.getItem('preferredColorMode')
    const systemDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    const mode = (saved === 'dark' || saved === 'light') ? saved : (systemDark ? 'dark' : 'light')
    root.classList.remove('my-app-dark', 'my-app-light')
    root.classList.add(mode === 'dark' ? 'my-app-dark' : 'my-app-light')
  } catch {
    // falls irgendwas schiefgeht -> licht
    document.documentElement.classList.add('my-app-light')
  }
})()

const app = createApp(App);

const i18n = createI18n();
app.provide('i18n', i18n);
app.config.globalProperties.$t = i18n.t;
app.config.globalProperties.$activeLang = i18n.activeLang;
app.config.globalProperties.$setLang = i18n.setLang;


app.use(PrimeVue, {
  ripple: true,
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: '.my-app-dark',
      lightModeSelector: '.my-app-light',
    }
  }
});

app.use(router);
app.use(ToastService);
app.use(ConfirmationService);
app.component('ConfirmDialog', ConfirmDialog);
app.use(VueGridLayout);
const pinia = createPinia();
app.use(pinia);

const unsavedChanges = useUnsavedChangesStore(pinia);
router.beforeEach((to, from, next) => {
  if (unsavedChanges.allowNavigation) {
    unsavedChanges.allowNavigation = false;
    unsavedChanges.clearAll();
    return next();
  }
  if (unsavedChanges.isDirty) {
    unsavedChanges.requestNavigation(to);
    return next(false);
  }
  return next();
});

app.component('DataTable', DataTable);
app.component('Column', Column);
app.component('MultiSelect', MultiSelect);
app.component('Tag', Tag);
app.component('InputIcon', InputIcon);
app.component('IconField', IconField);
app.component('Checkbox', Checkbox);
app.component('Select', Select);
app.component('Editor', Editor);
app.component('InputText', InputText);
app.component('pInputText', InputText);
app.component('pButton', pButton);
app.component('pCard', pCard);
app.component('pFloatLabel', pFloatLabel);
app.component('pDivider', pDivider);
app.component('pFieldset', pFieldset);
app.component('AppTable', AppTable);
app.component('AppTableWidget', AppTableWidget);
app.component('WidgetNumber', WidgetNumber);

app.directive('tooltip', pTooltip);

// ─── Token-Cache initialisieren BEVOR die App gemountet wird ─────────────────
// tokenStore.init() liest den Token async aus dem iOS-Keychain / localStorage.
// Danach liefert tokenStore.getSync() sofort → Router-Guards blockieren nicht.
// Ohne dieses await würde der erste Router-Guard einen leeren Cache sehen und
// den User direkt zur Login-Seite schicken (obwohl ein Token vorhanden ist).
//
// .catch(): Falls @capacitor/preferences nicht ladbar ist (z.B. fehlendes Plugin),
// mounten wir trotzdem — schlimmstenfalls landen wir auf /login.
tokenStore.init()
  .catch((err) => {
    console.warn('[main] tokenStore.init() failed, mounting anyway:', err)
  })
  .then(() => {
    app.mount('#app');

    /* Nach Mount: User+Settings laden, Store setzt Theme später korrekt */
    const userStore = useUserStore();
    userStore.fetchMe(api).catch(() => {
      // optional redirect to /login
    });
  });
